import React, { useState, useEffect } from 'react';
import { ExternalLink, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const ContributingArtistsDisplay = ({ 
    mediaType, 
    mediaId, 
    className = "",
    showTitle = true,
    layout = "horizontal", // "horizontal", "vertical", "compact"
    maxVisible = null // null for show all, number to limit visible artists
}) => {
    const [contributingArtists, setContributingArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        fetchContributingArtists();
    }, [mediaType, mediaId]);

    const fetchContributingArtists = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`/contributing-artists/${mediaType}/${mediaId}`);

            if (response.data.success) {
                setContributingArtists(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching contributing artists:', err);
            setError('Failed to load contributing artists');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={`flex items-center gap-2 ${className}`}>
                <div className="w-4 h-4 border border-gray-600 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-sm text-gray-400">Loading contributing artists...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`text-red-400 text-sm ${className}`}>
                {error}
            </div>
        );
    }

    if (contributingArtists.length === 0) {
        return null; // Don't show anything if no contributors - let parent handle display
    }

    // Determine which artists to show
    const visibleArtists = maxVisible && !showAll 
        ? contributingArtists.slice(0, maxVisible)
        : contributingArtists;
    
    const remainingCount = maxVisible && !showAll 
        ? Math.max(0, contributingArtists.length - maxVisible)
        : 0;

    // Compact layout for small spaces
    if (layout === "compact") {
        return (
            <div className={`inline-flex items-center gap-2 ${className}`}>
                <Users className="w-4 h-4 text-gray-400" />
                <div className="flex items-center gap-1">
                    {visibleArtists.map((artist, index) => (
                        <React.Fragment key={artist.artist_user_id}>
                            <Link
                                to={`/profile/${artist.username}`}
                                className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                {artist.display_name || artist.username}
                            </Link>
                            {index < visibleArtists.length - 1 && (
                                <span className="text-gray-500">,</span>
                            )}
                        </React.Fragment>
                    ))}
                    {remainingCount > 0 && (
                        <>
                            <span className="text-gray-500">,</span>
                            <button
                                onClick={() => setShowAll(true)}
                                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                +{remainingCount} more
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // Vertical layout
    if (layout === "vertical") {
        return (
            <div className={`space-y-3 ${className}`}>
                {showTitle && (
                    <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Contributing Artists
                    </h4>
                )}
                <div className="space-y-2">
                    {visibleArtists.map((artist) => (
                        <Link
                            key={artist.artist_user_id}
                            to={`/profile/${artist.username}`}
                            className="flex items-center gap-3 p-2 bg-gray-800/30 hover:bg-gray-800/60 rounded-lg transition-colors group"
                        >
                            {artist.profile_picture_url ? (
                                <img
                                    src={artist.profile_picture_url}
                                    alt={artist.display_name}
                                    width="40"
                                    height="40"
                                    className="w-10 h-10 rounded-full object-cover"
                                    style={{ background: 'transparent' }}
                                />
                            ) : (
                                <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
                                    <span className="text-xl leading-none">🌊</span>
                                </div>
                            )}
                            <div className="flex-1">
                                <div className="font-medium text-white group-hover:text-blue-300 transition-colors">
                                    {artist.display_name || artist.username}
                                </div>
                                <div className="text-sm text-gray-400">
                                    @{artist.username}
                                </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Link>
                    ))}
                    
                    {remainingCount > 0 && (
                        <button
                            onClick={() => setShowAll(true)}
                            className="w-full p-2 text-sm text-blue-400 hover:text-blue-300 bg-gray-800/20 hover:bg-gray-800/40 rounded-lg transition-colors border border-dashed border-gray-600"
                        >
                            Show {remainingCount} more contributing artist{remainingCount > 1 ? 's' : ''}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Default horizontal layout
    return (
        <div className={`space-y-3 ${className}`}>
            {showTitle && (
                <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contributing Artists
                </h4>
            )}
            
            <div className="flex flex-wrap gap-2">
                {visibleArtists.map((artist) => (
                    <Link
                        key={artist.artist_user_id}
                        to={`/${artist.username}`}
                        className="flex items-center gap-2 bg-gray-800/30 hover:bg-gray-800/60 rounded-full px-3 py-2 transition-colors group"
                    >
                        {artist.profile_picture_url ? (
                            <img
                                src={artist.profile_picture_url}
                                alt={artist.display_name}
                                width="24"
                                height="24"
                                className="w-6 h-6 rounded-full object-cover"
                                style={{ background: 'transparent' }}
                            />
                        ) : (
                            <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
                                <span className="text-xs leading-none">🌊</span>
                            </div>
                        )}
                        <span className="text-sm font-medium text-white group-hover:text-blue-300 transition-colors">
                            {artist.display_name || artist.username}
                        </span>
                        {artist.is_artist && (
                            <span className="text-xs text-purple-300 opacity-75">✨</span>
                        )}
                    </Link>
                ))}
                
                {remainingCount > 0 && (
                    <button
                        onClick={() => setShowAll(true)}
                        className="flex items-center gap-1 bg-gray-800/20 hover:bg-gray-800/40 rounded-full px-3 py-2 transition-colors border border-dashed border-gray-600"
                    >
                        <span className="text-sm text-blue-400">
                            +{remainingCount} more
                        </span>
                    </button>
                )}
            </div>

            {/* Created by info */}
            {visibleArtists.length > 0 && visibleArtists[0].created_by_username && (
                <div className="text-xs text-gray-500 mt-2">
                    Tagged by @{visibleArtists[0].created_by_username}
                </div>
            )}
        </div>
    );
};

export default ContributingArtistsDisplay;